import {
	CapTableCopyCellTextStyles,
	CopyCellText,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CopyCellText';
import { HoverColumnContext } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/HoverColumnContext';
import { EntityTags } from '@dealstack/frontend/app/pages/Project/Cap/common/EntityTags/EntityTags';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { getSecurityTypeNameFromType } from '@dealstack/shared/lib/common/securities/securities';
import { Badge, Checkbox, Flex, HoverCard } from '@mantine/core';
import { Cell, flexRender, Row, Table } from '@tanstack/react-table';
import React, { memo, useContext } from 'react';
import styled, { css } from 'styled-components';

import { CapTableRow, StaticColumn } from './capTableTypes';
import { generatePath, Link } from 'react-router-dom';
import { LegalPersonTags, SecurityType } from '@dealstack/shared/lib/generated';
import { useTranslationCapChart } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DEAL_ROUTES, PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';

export const CELL_MIN_HEIGHT = 50; //px

export const INSTRUMENT_COLUMN_WIDTH = 320;
export const TOTAL_SECURITIES_COLUMN_WIDTH = 160;
export const COLUMN_MIN_WIDTH = 180; //px
const HEADER_ROW_HEIGHT = 100; //px
const TABLE_PADDING_RIGHT = 32; //px

export const getLeftBorder = (color: string) => `
	&:after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 2px;
		height: 100%;
		background-color: ${color};
	}
`;

export const getBottomBorder = (color: string) => `
	&:before {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 2px;
		background-color: ${color};
	}
`;

export const TableColumnCellWithHoverContext = memo(
	({ cell, width }: { cell: Cell<CapTableRow, unknown>; width?: number }) => {
		const { setHoveredColId } = useContext(HoverColumnContext);

		const columnMeta: { className?: string } = cell.column.columnDef.meta ?? {};

		return (
			<td
				className={columnMeta.className}
				onMouseEnter={() => setHoveredColId(cell.column.id)}
				onMouseLeave={() => setHoveredColId(undefined)}
				style={width ? { width } : {}}
			>
				{flexRender(cell.column.columnDef.cell, cell.getContext())}
			</td>
		);
	}
);

interface Props {
	color?: string;
	$showBottomBorder?: boolean;
	$showLeftBorder?: boolean;
	$shouldHighlight?: boolean;
	children: React.ReactNode | React.ReactNode[];
}

export const TableHeaderCellWithHoverContext = memo(
	({ columnId, children, ...props }: Props & { columnId: string }) => {
		const { hoveredColId } = useContext(HoverColumnContext);
		return (
			<TableHeaderCell {...props} $shouldHighlight={hoveredColId === columnId}>
				{children}
			</TableHeaderCell>
		);
	}
);

export const TableHeaderCell = memo(styled.th<Props>`
	font-size: 0.875rem;
	line-height: 1.25rem;
	font-weight: 600;
	text-align: left;

	position: relative;

	${({ color, $showLeftBorder = true }) => color && $showLeftBorder && getLeftBorder(color)};
	${({ color, $showBottomBorder }) => color && $showBottomBorder && getBottomBorder(color)};
	${({ $shouldHighlight }) =>
		$shouldHighlight &&
		css`
			background-color: ${(props) => props.theme.v3.colors.blue[0]};
		`};
`);

export const TableRow = styled.tr<{
	$isEntityRow: boolean;
	$isMipRow: boolean;
	color: string;
}>`
	.${StaticColumn.INSTRUMENT} {
		position: relative;
		background-color: ${({ theme }) => theme.v3.colors.white[0]};

		${({ color }) => color && getLeftBorder(color)};

		&:after {
			height: calc(100% + 1px);
		}
	}

	.${StaticColumn.TOTAL_SECURITIES} {
		position: relative;
		background-color: ${({ theme }) => theme.v3.colors.white[0]};
		z-index: 1;

		&:after {
			content: '';
			position: absolute;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 0.5px; /* Super thin border */
			background-color: ${(props) => props.theme.v3.colors.gray[4]};
			z-index: 2;
		}
	}

	${({ $isMipRow }) =>
		$isMipRow &&
		css`
			.${StaticColumn.INSTRUMENT}, .${StaticColumn.TOTAL_SECURITIES}, & {
				background-color: ${(props) => props.theme.v3.colors.gray[2]};
			}

			font-size: 0.875rem;
			line-height: 1.25rem;
			font-weight: 600;
		`};

	${({ $isEntityRow }) =>
		$isEntityRow &&
		css`
			.${StaticColumn.INSTRUMENT}, .${StaticColumn.TOTAL_SECURITIES}, & {
				background-color: ${(props) => props.theme.v3.colors.gray[0]};
			}

			font-size: 0.875rem;
			line-height: 1.25rem;
			font-weight: 600;
		`}

	&:hover {
		.${StaticColumn.INSTRUMENT} {
			background-color: ${(props) => props.theme.v3.colors.blue[0]};
		}
	}

	td:hover {
		background-color: ${(props) => props.theme.v3.colors.blue[0]};
	}
`;

export const TablePage = memo(styled.div<{ $showTableShadow?: boolean }>`
	position: relative;
	height: 100%;

	//shadow for static columns
	${(props) => {
		if (props.$showTableShadow) {
			return css`
				&:after {
					content: '';
					position: absolute;
					width: calc(${INSTRUMENT_COLUMN_WIDTH}px + ${TOTAL_SECURITIES_COLUMN_WIDTH}px);
					pointer-events: none;
					height: 100%;
					left: 0;
					top: 0;
					box-shadow: 0 8px 16px 0 #b8bfc2;
					z-index: 2;
				}
			`;
		}
	}}

	${InlineLoader} {
		width: calc(100% + ${TABLE_PADDING_RIGHT}px);
		min-height: 10rem;
	}
`);

export const TableWrapper = memo(styled.div<{ $maxHeight?: number; $bodyHeight?: number }>`
	width: calc(100% + ${TABLE_PADDING_RIGHT}px);
	max-height: ${(props) => props.$maxHeight}px;
	overflow: auto;

	table {
		width: 100%;

		thead {
			position: sticky;
			top: 0;
			z-index: 1;

			tr {
				min-height: ${HEADER_ROW_HEIGHT}px;
				background-color: ${(props) => props.theme.v3.colors.gray[2]};
				display: flex;
				width: 100%;

				//static cells must not be transparent
				th.${StaticColumn.INSTRUMENT}, th.${StaticColumn.TOTAL_SECURITIES} {
					background-color: ${(props) => props.theme.v3.colors.gray[2]};
				}

				th {
					min-height: ${HEADER_ROW_HEIGHT}px;
					max-height: ${HEADER_ROW_HEIGHT}px;
				}
			}
		}

		tbody {
			height: ${(props) => props.$bodyHeight};
			position: relative;
		}

		tbody tr {
			z-index: 0;
			display: flex;
			position: absolute;
			width: 100%;
		}

		tbody tr,
		thead {
			border-bottom: 1px solid ${(props) => props.theme.v3.colors.gray[4]};
		}

		th,
		td {
			padding: 0.5rem;
			line-height: 1.5rem;
			min-width: ${COLUMN_MIN_WIDTH}px;
			max-width: ${COLUMN_MIN_WIDTH}px;

			&.${StaticColumn.INSTRUMENT} {
				width: ${INSTRUMENT_COLUMN_WIDTH}px;
				min-width: ${INSTRUMENT_COLUMN_WIDTH}px;
				max-width: ${INSTRUMENT_COLUMN_WIDTH}px;
				position: sticky;
				left: 0;
				z-index: 1;
			}

			&.${StaticColumn.TOTAL_SECURITIES} {
				width: ${TOTAL_SECURITIES_COLUMN_WIDTH}px;
				min-width: ${TOTAL_SECURITIES_COLUMN_WIDTH}px;
				max-width: ${TOTAL_SECURITIES_COLUMN_WIDTH}px;
				position: sticky;
				left: ${INSTRUMENT_COLUMN_WIDTH}px;
				z-index: 1;
			}
		}

		td {
			vertical-align: middle;
		}

		td:not(.${StaticColumn.INSTRUMENT}) {
			text-align: right;
		}

		:root {
			--cap-table-virtual-padding-left: 0px;
			--cap-table-virtual-padding-right: 0px;
		}

		.left-column-spacer {
			width: var(--cap-table-virtual-padding-left);
			max-width: var(--cap-table-virtual-padding-left);
			min-width: var(--cap-table-virtual-padding-left);
			padding: unset;
		}

		.right-column-spacer {
			max-width: var(--cap-table-virtual-padding-right);
			width: var(--cap-table-virtual-padding-right);
			min-width: var(--cap-table-virtual-padding-right);
			padding: unset;
		}
	}

	${CapTableCopyCellTextStyles}
`);

export const MipNameCell = memo(
	({ name, row, isExpanded }: { row: Row<CapTableRow>; name: string; isExpanded: boolean }) => {
		return (
			<Flex gap="0.5rem">
				<Flex direction="column" gap="0.25rem" sx={{ flex: 1 }}>
					<CopyCellText iconPlacement="right" text={name}>
						{name}
					</CopyCellText>
					<Flex gap="0.25rem" wrap="wrap">
						<MipGroupBadge />
						<ManagementLoanBadge />
					</Flex>
				</Flex>

				{row.getCanExpand() ? (
					<div data-html2canvas-ignore={true}>
						<Icon
							onClick={() => row.toggleExpanded()}
							size="lg"
							variant={isExpanded ? 'caret-down' : 'caret-right'}
							style={{ marginLeft: 'auto', marginTop: '0.3rem' }}
							data-html2canvas-ignore
						/>
					</div>
				) : null}
			</Flex>
		);
	}
);

export const IssuerNameCell = memo(
	({
		name,
		tags,
		row,
		id,
		isExpanded,
	}: {
		row: Row<CapTableRow>;
		tags: LegalPersonTags[];
		name: string;
		id?: string;
		isExpanded: boolean;
	}) => {
		const issuerPath = generatePath(`..${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`, {
			personId: id ?? '',
		});
		return (
			<Flex gap="0.5rem">
				<Flex direction="column" gap="0.25rem" sx={{ flex: 1 }}>
					<CopyCellText iconPlacement="right" text={name} isIssuer={true}>
						<Link to={issuerPath}>{name}</Link>
					</CopyCellText>

					<EntityTags tags={tags} visibleTagCount={1} blockDropdown={true} />
				</Flex>

				{row.getCanExpand() ? (
					<div data-html2canvas-ignore={true}>
						<Icon
							onClick={() => row.toggleExpanded()}
							size="lg"
							variant={isExpanded ? 'caret-down' : 'caret-right'}
							style={{ marginLeft: 'auto', marginTop: '0.3rem' }}
							data-html2canvas-ignore
						/>
					</div>
				) : null}
			</Flex>
		);
	}
);

export const SecurityTypeNameCell = memo(
	({
		type,
		row,
		showCheckbox,
		isExpanded,
	}: {
		row: Row<CapTableRow>;
		type: SecurityType;
		showCheckbox: boolean;
		isExpanded: boolean;
	}): JSX.Element => {
		const securityTypeName = getSecurityTypeNameFromType(type);

		return (
			<Flex gap="0.5rem" align="center">
				{showCheckbox && (
					<Checkbox
						checked={row.getIsAllSubRowsSelected()}
						disabled={!row.getCanSelect()}
						indeterminate={row.getIsSomeSelected()}
						onChange={() => row.toggleSelected()}
					/>
				)}

				<CopyCellText iconPlacement="right" text={securityTypeName as string}>
					{securityTypeName}
				</CopyCellText>

				{row.getCanExpand() && (
					<div data-html2canvas-ignore={true}>
						<Icon
							onClick={() => row.toggleExpanded()}
							size="lg"
							variant={isExpanded ? 'caret-down' : 'caret-right'}
							style={{ marginLeft: 'auto', marginTop: '0.3rem' }}
							data-html2canvas-ignore
						/>
					</div>
				)}
			</Flex>
		);
	}
);

export const SecurityNameCell = memo(styled(
	({
		name,
		row,
		showCheckbox,
		className,
	}: {
		row: Row<CapTableRow>;
		name: string;
		showCheckbox: boolean;
		className?: string;
	}) => {
		return (
			<div className={className}>
				{showCheckbox && (
					<Checkbox
						checked={row.getIsSelected()}
						disabled={!row.getCanSelect()}
						onChange={row.getToggleSelectedHandler()}
					/>
				)}

				<CopyCellText iconPlacement="right" text={name}>
					{name}
				</CopyCellText>
			</div>
		);
	}
)<{ color?: string }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-left: 1.25rem;
	${({ color }) => color && getLeftBorder(color)}

	//margin for left border
    &:after {
		left: 1rem;
	}
`);

export const MipGroupBadge = memo(() => {
	const tCapChart = useTranslationCapChart();

	return (
		<Badge
			size="sm"
			variant="outline"
			color="dark.04"
			c="dark"
			fw={400}
			sx={{ display: 'block' }}
		>
			{tCapChart('inv_programme')}
		</Badge>
	);
});

export const ManagementLoanBadge = memo(() => {
	const tCapChart = useTranslationCapChart();

	return (
		<Badge
			size="sm"
			variant="outline"
			color="dark.04"
			c="dark"
			fw={400}
			sx={{ display: 'block' }}
		>
			{tCapChart('management_loan')}
		</Badge>
	);
});

export const isCapTableRowSelected = (table: Table<CapTableRow>, row: Row<CapTableRow>) => {
	const selectedRowIds = table.getSelectedRowModel().flatRows?.map((r) => r.id);
	return !!selectedRowIds.find((id) => id === row.id);
};

export const TwoLineClamp = styled.div`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.25rem;
	max-height: 2.5rem;
`;

export const EllipsizedHeader = ({
	short,
	full,
	dropdownMaxWidth = 360,
}: {
	short: React.ReactNode;
	full: React.ReactNode;
	dropdownMaxWidth?: number;
}) => {
	return (
		<HoverCard
			withinPortal
			openDelay={150}
			closeDelay={120}
			position="bottom-start"
			shadow="md"
			withArrow
		>
			<HoverCard.Target>
				<TwoLineClamp>{short}</TwoLineClamp>
			</HoverCard.Target>

			<HoverCard.Dropdown maw={dropdownMaxWidth}>
				<div style={{ maxHeight: '40vh', overflowY: 'auto' }}>{full}</div>
			</HoverCard.Dropdown>
		</HoverCard>
	);
};
